import React, { useState } from 'react';
import { Button, Image } from 'react-bootstrap';
import { GiAirplaneDeparture } from 'react-icons/gi';
import { FiTrendingDown, FiTrendingUp } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { CustomImageHandler } from '../../../../../../../../shared/components/CustomImageHandler';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { Totals } from '../contract/Totals';
import { calcFee, formatDate, getTotals } from '../../../../../../../../shared/functions/Functions';
import { ContractHistoryAdditionalItemsTable } from './ContractHistoryAdditionalItemsTable';
import { ContractHistoryOfferPlatformTable } from './ContractHistoryOfferPlatformTable';
import { ContractHistoryOfferHeader } from './ContractHistoryOfferHeader';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../../../../../store/rootReducer';
import { TotalTypes } from '../../../../../../../../enum/TotalTypes';
import { IContract } from '../../../../../../../../model/shared/IContract';
import { InfluencerFeeTotals } from '../../../../../../../../shared/components/offer/InfluencerFeeTotals';
import { getTruncatedData } from '../../../../../../../../utils/helpers/getTruncatedData';
import TooltipOverlay from '../../../../../../../../shared/components/tooltip/Tooltip';
import FilePlaceholder from '../../../../../../../../shared/components/placeholder/FilePlaceholder';
import { Fancybox } from '../../../../../../../../shared/components/Fancybox';
import { Fancybox as NativeFancybox } from '@fancyapps/ui';
import { downloadFile } from '../../../../../../../../shared/functions/Functions';
import { ContractAnnexInfo } from '../../../../../../../../shared/components/offer/ContractAnnexInfo';
import ItemValueSection from '../../../../../../../../shared/components/offer/ItemValueSection';
import { priceSplitter } from '../../../../../../../../utils/helpers/priceSplitter';

interface INegotiationHistoryDetailProps {
    contract?: IContract;
    // The version before this one, the price is compared with it
    previousContract?: IContract;
    isCharity: boolean;
}

// The price shown on the card of a version
const getDisplayedPrice = (contract: IContract | undefined, isTransparentPricing?: boolean) => {
    const subTotal = getTotals(contract?.additionalContractItems, contract?.totalPlatforms) ?? 0;
    if (contract?.hasInfluencerFee) {
        return Math.round(subTotal - calcFee(subTotal, contract.influencerFeePercentage));
    }
    return Math.round((isTransparentPricing ? contract?.totalWithFee : subTotal) ?? 0);
};

const ContractHistoryDetail = ({ contract, previousContract, isCharity }: INegotiationHistoryDetailProps) => {
    const [expended, setExpended] = useState(false);
    const { t } = useTranslation();
    const { isTransparentPricing, campaign } = useSelector((state: IAllStates) => state.contractBrand);
    const feePercentage = Math.round((1 - Number(campaign?.feePercentage ?? 0.0)) * 100);
    const subTotalSum = getTotals(contract?.additionalContractItems, contract?.totalPlatforms);
    const grandTotalForDisplay = isTransparentPricing ? contract?.totalWithFee : subTotalSum;
    const legalDocument = contract?.legalDocuments?.[0];
    const { truncatedName: truncatedFileName, isTruncated: isFileNameTruncated } = getTruncatedData(
        legalDocument?.originalName ?? '',
        30
    );
    const { truncatedName: truncatedUploaderName, isTruncated: isUploaderNameTruncated } = getTruncatedData(
        legalDocument?.uploadedBy?.displayName ?? '',
        30
    );

    const influencerFeePercent = contract?.influencerFeePercentage;
    const hasInfluencerFee = contract?.hasInfluencerFee;
    const isIvConnectedClub = contract?.isIvConnectedClub;
    const influenceFeeSum = hasInfluencerFee ? calcFee(subTotalSum ?? 0, influencerFeePercent) : 0;

    // Marks the accepted version (and the ones an amendment replaced) among the negotiation rounds
    const isInEffectOrReplaced = ['accepted', 'completed', 'annexed'].includes(contract?.status ?? '');

    const grandTotalMinusInfluencerFee = Math.round((subTotalSum ?? 0) - influenceFeeSum);

    const previousPrice = previousContract ? getDisplayedPrice(previousContract, isTransparentPricing) : 0;
    const priceChange =
        previousPrice > 0
            ? ((getDisplayedPrice(contract, isTransparentPricing) - previousPrice) / previousPrice) * 100
            : 0;
    const roundedPriceChange = Math.round(Math.abs(priceChange) * 10) / 10;

    // The click must not open or close the accordion
    const downloadPdf = (e: React.MouseEvent) => {
        e.stopPropagation();
        downloadFile(contract?.pdf?.path ?? '', `contract-${contract?.id}.pdf`);
    };

    // Who made this version: the counter side, or the user the contract belongs to
    const author = contract?.counterContractBy ?? contract?.user;
    const isSupervisor = !!contract?.counterContractBy?.isSupervisor;
    const authorFullName = `${author?.firstName ?? ''} ${author?.lastName ?? ''}`.trim();
    const authorName = isSupervisor ? 'Customer Success by i.v.' : author?.displayName?.trim() || authorFullName;
    const authorPhoto = isSupervisor ? '' : author?.profilePhoto?.thumbnail ?? '';

    const isSender = contract?.user?.id === contract?.counterContractBy?.id || !contract?.counterContractBy;
    const dotClass = isInEffectOrReplaced ? 'is-signed' : contract?.status === 'pre_annex' ? 'is-pending' : '';

    return (
        <div className={`history-timeline-item ${dotClass}`}>
            <div className="history-timeline-meta">
                {isSender
                    ? t('brand.campaign.book.offer.modal.sent_at')
                    : t('brand.campaign.book.offer.modal.received_at')}{' '}
                - {formatDate(contract?.createdAt ?? '')}
            </div>
            <Accordion className="mt-1" expanded={expended}>
                <AccordionSummary className="p-0 border-0" onClick={() => setExpended(!expended)}>
                    {/* On mobile the author and the price are stacked, so the whole width of the card is used */}
                    <div
                        className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between default-radius bg-light py-2 px-3 position-relative w-100"
                        style={{ gap: 8 }}
                    >
                        <div className="d-flex align-items-center">
                            <CustomImageHandler
                                altTag={'User Profile'}
                                classes="rounded-img-sm"
                                style={{ width: 40, height: 40 }}
                                thumbnailPath={authorPhoto}
                                photoPath={authorPhoto}
                                name={authorName}
                            />
                            <div className="ml-3">
                                <h6 className="mb-0">{authorName}</h6>
                                <span className="text-muted font-12">
                                    {contract?.isAnnex
                                        ? `${t('brand.campaign.book.contract.modal.annex.label')} #${
                                              contract.annexNumber
                                          } ${t('brand.campaign.book.contract.modal.annex.to_contract')} #${
                                              contract.annexOf?.id
                                          }`
                                        : `${t('brand.campaign.book.contract.modal.contract_id')} #${contract?.id}`}
                                </span>
                            </div>
                        </div>
                        {contract?.pdf?.path && (
                            <Button
                                variant="light"
                                className="btn-outline-primary btn-sm d-lg-none position-absolute d-inline-flex align-items-center"
                                style={{ top: 8, right: 12 }}
                                onClick={downloadPdf}
                            >
                                {t('general.download')}
                            </Button>
                        )}
                        <div
                            className="d-flex align-items-center justify-content-between justify-content-lg-end"
                            style={{ gap: 16 }}
                        >
                            <div className="text-lg-end">
                                <div className="d-flex align-items-center justify-content-lg-end">
                                    <h6 className="mb-0 text-primary euro-symbol">
                                        {priceSplitter(getDisplayedPrice(contract, isTransparentPricing))}
                                    </h6>
                                    {!!contract?.additionalContractItems?.length && (
                                        <TooltipOverlay
                                            content={t('brand.campaign.book.contract.additional_costs_included')}
                                            className="ml-1"
                                        >
                                            <GiAirplaneDeparture className="text-primary font-14" />
                                        </TooltipOverlay>
                                    )}
                                </div>
                                {roundedPriceChange > 0 && (
                                    <TooltipOverlay
                                        content={t('brand.campaign.book.contract.modal.price_change')}
                                        dFlex={false}
                                    >
                                        <span
                                            className={`font-weight-semi-bold ${
                                                priceChange > 0 ? 'text-success' : 'text-danger'
                                            }`}
                                            style={{ fontSize: 11 }}
                                        >
                                            {priceChange > 0 ? (
                                                <FiTrendingUp className="mr-1" />
                                            ) : (
                                                <FiTrendingDown className="mr-1" />
                                            )}
                                            {String(roundedPriceChange).replace('.', ',')}%
                                        </span>
                                    </TooltipOverlay>
                                )}
                            </div>
                            {(isInEffectOrReplaced || contract?.isAnnex) && (
                                <p className={`mb-0 text-nowrap status ${contract?.status} text-dark`}>
                                    {contract?.isAnnex
                                        ? `${t('brand.campaign.book.contract.modal.annex.label')} ${
                                              contract.annexNumber
                                          }`
                                        : t(`general.offerStatus.${contract?.status}`)}
                                </p>
                            )}
                            <div className="d-flex align-items-center">
                                {contract?.pdf?.path && (
                                    <Button
                                        variant="light"
                                        className="btn-outline-primary btn-sm d-none d-lg-inline-flex align-items-center mr-3"
                                        onClick={downloadPdf}
                                    >
                                        {t('general.download')}
                                    </Button>
                                )}
                                <Image
                                    src="/icons/img-double-down.png"
                                    alt="Arrow down img"
                                    className={`${expended ? 'rotate-180' : ''} rotate-smooth`}
                                    height={15}
                                />
                            </div>
                        </div>
                    </div>
                </AccordionSummary>
                <AccordionDetails className="p-0 ">
                    <ContractAnnexInfo contract={contract} compact />
                    <ContractHistoryOfferPlatformTable totalPlatforms={contract?.totalPlatforms} />
                    {contract?.additionalContractItems?.length !== 0 && (
                        <>
                            <ContractHistoryAdditionalItemsTable additionalItems={contract?.additionalContractItems} />
                            <Totals
                                title={TotalTypes.ADDITIONAL_ITEMS}
                                price={getTotals(contract?.additionalContractItems)}
                                displayPlus={true}
                            />
                        </>
                    )}
                    {isTransparentPricing && !isCharity && (
                        <div className="w-100 mt-3">
                            <Totals
                                title={TotalTypes.SUBTOTAL}
                                price={subTotalSum}
                                feePercentage={100 - feePercentage}
                            />
                        </div>
                    )}
                    {isTransparentPricing && (
                        <div className="w-100 mt-3">
                            <Totals
                                title={TotalTypes.FEE}
                                price={contract?.feeAmount ?? 0}
                                feePercentage={feePercentage}
                                displayPlus={true}
                            />
                        </div>
                    )}
                    {(!isCharity || (isCharity && grandTotalMinusInfluencerFee > 0)) && (
                        <InfluencerFeeTotals
                            subTotalAndAddons={subTotalSum ?? 0}
                            serviceFeeSum={Math.round(influenceFeeSum)}
                            containerClassName="pt-3"
                            grandTotalSum={grandTotalMinusInfluencerFee}
                            feePercentage={influencerFeePercent}
                            hasFee={hasInfluencerFee}
                            isIvConnectedClub={isIvConnectedClub}
                        />
                    )}
                    {!hasInfluencerFee && (
                        <div className="mt-3">
                            <Totals
                                title={TotalTypes.GRAND}
                                price={grandTotalForDisplay}
                                feePercentage={isTransparentPricing ? 100 : undefined}
                            />
                        </div>
                    )}
                    {isCharity && !!campaign?.charityValueDesc && (
                        <ItemValueSection description={campaign?.charityValueDesc} />
                    )}
                    <ContractHistoryOfferHeader personalOffer={contract?.personalOffer} />
                    {legalDocument && (
                        <div className="mt-3">
                            <div
                                className="d-flex align-items-start p-2 border rounded position-relative"
                                style={{ maxWidth: 300 }}
                            >
                                <Fancybox>
                                    <FilePlaceholder
                                        file={legalDocument}
                                        hideText={true}
                                        classIcon="font-22"
                                        styleContent={{ width: 40, height: 40, flexShrink: 0 }}
                                        nameFancybox="legal-document"
                                        handleDownload={() =>
                                            downloadFile(legalDocument?.path ?? '', legalDocument?.originalName)
                                        }
                                        right={-10}
                                    />
                                </Fancybox>
                                <div className="ms-2">
                                    <TooltipOverlay
                                        content={isFileNameTruncated ? legalDocument?.originalName : ''}
                                        dFlex={false}
                                    >
                                        <p
                                            className="mb-0 fw-semibold cursor-pointer"
                                            onClick={() =>
                                                NativeFancybox.show([
                                                    {
                                                        src: legalDocument?.path,
                                                        type: 'iframe',
                                                    },
                                                ])
                                            }
                                        >
                                            {truncatedFileName || '-'}
                                        </p>
                                    </TooltipOverlay>
                                    <p className="mb-0 text-muted fs-12">
                                        {t('influencer.offer_management.modal.uploaded_by')}:{' '}
                                        <TooltipOverlay
                                            content={
                                                isUploaderNameTruncated ? legalDocument?.uploadedBy?.displayName : ''
                                            }
                                            dFlex={false}
                                        >
                                            <span>{truncatedUploaderName || '-'}</span>
                                        </TooltipOverlay>
                                    </p>
                                    <p className="mb-0 text-muted fs-12">
                                        {t('influencer.offer_management.modal.uploaded_at')}:{' '}
                                        {formatDate(legalDocument?.updatedAt)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </AccordionDetails>
            </Accordion>
        </div>
    );
};

export default ContractHistoryDetail;
